import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSocket } from '../../store/SocketContext';
import { Loader2, Pencil, User, X, ArrowRight, ArrowLeftRight, Target, Monitor, Smartphone, QrCode } from 'lucide-react';
import ParticipantExperience from './ParticipantExperience';
import ThemeToggle from '../../components/ThemeToggle';
import Tooltip from '../../components/ui/Tooltip';
import { API_PATH, request } from '../../config/api';
import { getParticipantSessionId } from '../../utils/participantSession';
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport';
import styles from './JoinPage.module.css';
import livePulseLogo from '../../assets/live-pulse-logo.png';

const API = API_PATH;
const STORAGE_KEYS = {
    step: 'lp_step',
    code: 'lp_code',
    name: 'lp_name',
    event: 'lp_event',
    voteAnonymous: 'lp_vote_anonymous',
    previousEvents: 'lp_previous_events',
    desktopMode: 'lp_desktop_mode',
};

// Ensure activity.content is always an object (API/socket may send string for JSONB)
function normalizeActivity(activity) {
    if (!activity) return activity;
    const raw = activity.content;
    if (raw == null) return activity;
    if (typeof raw === 'object') return activity;
    if (typeof raw === 'string') {
        try {
            return { ...activity, content: JSON.parse(raw) };
        } catch {
            return activity;
        }
    }
    return activity;
}

// ─── Validation Helpers ──────────────────────────────────────────────────
const validateName = (name, voteAsAnonymous = false) => {
    if (voteAsAnonymous) return ''; // Name not required when voting anonymously
    if (!name.trim()) return 'Name is required';
    if (name.trim().length < 2) return 'Name must be at least 2 characters';
    return '';
};

const validateCode = (code) => {
    if (!code.trim()) return 'Event code is required';
    if (code.trim().length !== 6) return 'Event code must be exactly 6 characters';
    return '';
};

export const JoinPagePreview = ({
    event,
    activity,
    step,
    name = 'Preview User',
    participantsCount = 0,
}) => {
    const previewStep = step || (activity ? 'activity' : 'waiting');

    return (
        <div className={`${styles.page} ${styles.previewPage}`}>
            <div className={styles.topBar}>
                <div className={styles.brandContainer} aria-hidden>
                    <img src={livePulseLogo} alt="LivePulse" className={styles.brand} />
                </div>
                {event && previewStep !== 'code' && (
                    <div className={styles.liveCounter}>
                        <span className={styles.liveDot} /> {participantsCount} connected
                    </div>
                )}
            </div>
            <div className={styles.pageBody}>
                {previewStep !== 'code' && (
                    <ParticipantExperience
                        step={previewStep}
                        name={name}
                        event={event}
                        activity={activity}
                        responses={[]}
                        onSubmit={() => { }}
                        onEditResponse={() => { }}
                        isPreview={true}
                    />
                )}
            </div>
        </div>
    );
};

const JoinPage = () => {
    const { eventCode } = useParams();
    const navigate = useNavigate();
    const socket = useSocket();
    
    const [step, setStep] = useState(() => sessionStorage.getItem(STORAGE_KEYS.step) || 'code');
    const [code, setCode] = useState(() => eventCode || sessionStorage.getItem(STORAGE_KEYS.code) || '');
    const [name, setName] = useState(() => sessionStorage.getItem(STORAGE_KEYS.name) || '');
    const [voteAsAnonymous, setVoteAsAnonymous] = useState(() =>
        sessionStorage.getItem(STORAGE_KEYS.voteAnonymous) === 'true'
    );
    const [error, setError] = useState('');
    const [event, setEvent] = useState(() => {
        const saved = sessionStorage.getItem(STORAGE_KEYS.event);
        return saved ? JSON.parse(saved) : null;
    });
    const [activity, setActivity] = useState(null);
    const activityRef = useRef(null);
    useEffect(() => { activityRef.current = activity; }, [activity]);

    // ─── Desktop mode ───────────────────────────────────────────────────────
    // On a real phone this page is already full-bleed; on a laptop/desktop it's normally
    // shown inside a small centered "phone frame" (see JoinPage.module.css) so it reads as
    // "this is what your phone will show" — nice for a quick look, cramped for someone
    // actually answering polls from a big screen. Non-mobile visitors can opt into a wider
    // two-column layout instead; remembered per-browser so they don't have to reselect it
    // every visit.
    const isMobileViewport = useIsMobileViewport();
    const [desktopMode, setDesktopMode] = useState(() => {
        try { return localStorage.getItem(STORAGE_KEYS.desktopMode) === 'true'; } catch { return false; }
    });
    const toggleDesktopMode = () => {
        setDesktopMode(prev => {
            const next = !prev;
            try { localStorage.setItem(STORAGE_KEYS.desktopMode, String(next)); } catch { /* ignore */ }
            return next;
        });
    };
    const showDesktopLayout = desktopMode && !isMobileViewport;
    const joinUrl = event?.code ? `${window.location.origin}/join/${event.code}` : '';

    // ─── Form validation state ──────────────────────────────────────────────
    const [fieldErrors, setFieldErrors] = useState({});
    const [touched, setTouched] = useState({});

    // ─── Profile (name + anonymous) modal ────────────────────────────────────
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [profileName, setProfileName] = useState('');
    const [profileAnonymous, setProfileAnonymous] = useState(false);

    // ─── Switch Event state ─────────────────────────────────────────────────
    const [showSwitchModal, setShowSwitchModal] = useState(false);
    const [switchCode, setSwitchCode] = useState('');
    const [switchError, setSwitchError] = useState('');
    const [switchLoading, setSwitchLoading] = useState(false);
    const [previousEvents, setPreviousEvents] = useState(() => {
        const saved = sessionStorage.getItem(STORAGE_KEYS.previousEvents);
        return saved ? JSON.parse(saved) : [];
    });

    const [responses, setResponses] = useState([]);
    const [participantsCount, setParticipantsCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isSocketConnected, setIsSocketConnected] = useState(false);
    const [userAnswer, setUserAnswer] = useState(null);
    const codeRef = useRef();

    useEffect(() => {
        if (!code) codeRef.current?.focus();
    }, [code]);

    // Fetch results if they should be visible
    const fetchResults = (actId) => {
        if (!event || !actId) return;
        request(`${API}/join/${event.code}/results/${actId}`)
            .then(r => r.json())
            .then(data => {
                if (Array.isArray(data)) {
                    const parsed = data.map(r => ({
                        ...r,
                        answer: typeof r.answer === 'string' ? JSON.parse(r.answer) : r.answer
                    }));
                    setResponses(parsed);
                }
            })
            .catch(console.error);
    };

    // Emoji reaction (Open Text / Audience Q&A) — participant only
    const handleReact = useCallback(async (responseId, emoji) => {
        if (!event?.code || !activity?.id) return;
        try {
            const res = await request(`${API}/join/${event.code}/react`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ activityId: activity.id, responseId, emoji, participant: name, sessionId: getParticipantSessionId() })
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || 'Failed to add reaction');
            }
        } catch (e) {
            console.error(e);
            setError('Failed to add reaction');
        }
    }, [event?.code, activity?.id, name]);

    // ─── Form handlers ──────────────────────────────────────────────────────
    const handleNameChange = (e) => {
        const val = e.target.value;
        setName(val);
        if (touched.name) {
            const err = validateName(val, voteAsAnonymous);
            setFieldErrors(prev => ({ ...prev, name: err }));
        }
    };

    const handleCodeChange = (e) => {
        const val = e.target.value.toUpperCase();
        setCode(val);
        if (touched.code) {
            const err = validateCode(val);
            setFieldErrors(prev => ({ ...prev, code: err }));
        }
    };

    const handleFieldBlur = (field) => {
        setTouched(prev => ({ ...prev, [field]: true }));
        let err = '';
        if (field === 'name') err = validateName(name, voteAsAnonymous);
        if (field === 'code') err = validateCode(code);
        setFieldErrors(prev => ({ ...prev, [field]: err }));
    };

    // Initial results fetch when activity changes or results revealed
    useEffect(() => {
        if (activity && activity.content?.settings?.pollResults === 'visible') {
            fetchResults(activity.id);
        }
    }, [activity?.id, activity?.content?.settings?.pollResults]);

    // Listen for admin launching a new activity, room updates, and new responses
    useEffect(() => {
        if (!socket || !event) return;

        console.log('[JoinPage] Setting up socket listeners for event:', event.id);

        // Check if socket is already connected
        if (socket.connected) {
            console.log('[JoinPage] Socket already connected');
            setIsSocketConnected(true);
        }

        // Join the event room
        socket.emit('participant:join', { eventId: event.id });

        // Room update handler
        const handleRoomUpdate = ({ count }) => {
            console.log('[JoinPage] Room update:', count);
            setParticipantsCount(count);
        };

        // Activity launched handler
        const handleActivityLaunched = ({ activity: newActivity }) => {
            console.log('[JoinPage] Activity launched:', newActivity);
            setActivity(normalizeActivity(newActivity));
            setResponses([]);
            setUserAnswer(null);
            setStep('activity');
        };

        // Activity stopped handler
        const handleActivityStopped = () => {
            console.log('[JoinPage] Activity stopped');
            setActivity(null);
            setResponses([]);
            setUserAnswer(null);
            setStep('waiting');
        };

        // Activity deleted handler — admin deleted the poll currently on screen. This socket
        // never drops in this scenario, so the reconnect-resync above doesn't cover it; without
        // this listener the participant would keep showing (and could keep answering) a poll
        // that no longer exists.
        const handleActivityDeleted = ({ activityId }) => {
            if (!activityRef.current || Number(activityRef.current.id) !== Number(activityId)) return;
            console.log('[JoinPage] Active activity deleted');
            setActivity(null);
            setResponses([]);
            setUserAnswer(null);
            setStep('waiting');
        };

        // Activity updated handler
        const handleActivityUpdated = ({ activity: updatedActivity }) => {
            console.log('[JoinPage] Activity updated:', updatedActivity);
            setActivity(prev => {
                if (prev && prev.id === updatedActivity.id) {
                    return normalizeActivity(updatedActivity);
                }
                return prev;
            });
        };

        // Results cleared handler
        const handleResultsCleared = ({ activityId }) => {
            console.log('[JoinPage] Results cleared for activity:', activityId);
            if (activityRef.current && Number(activityRef.current.id) === Number(activityId)) {
                setResponses([]);
                setStep('activity');
            }
        };

        // Response deleted (admin deleted one) — refetch results so presenter & participant stay in sync
        const handleResponseDeleted = ({ activityId }) => {
            if (activityRef.current && Number(activityRef.current.id) === Number(activityId)) {
                fetchResults(activityId);
            }
        };

        // Emoji reaction added — update that response's reactions in real time
        const handleResponseReaction = ({ activityId: aid, responseId, reactions }) => {
            if (activityRef.current && Number(activityRef.current.id) !== Number(aid)) return;
            setResponses(prev => prev.map(r => r.id != null && Number(r.id) === Number(responseId) ? { ...r, reactions } : r));
        };

        // Response answer updated (e.g. reply added, or approved for Audience Q&A)
        const handleResponseUpdated = ({ activityId: aid, responseId, answer: rawAnswer }) => {
            if (activityRef.current && Number(activityRef.current.id) !== Number(aid)) return;
            const answer = typeof rawAnswer === 'string' ? (() => { try { return JSON.parse(rawAnswer); } catch { return rawAnswer; } })() : rawAnswer;
            setResponses(prev => prev.map(r => r.id != null && Number(r.id) === Number(responseId) ? { ...r, answer } : r));
        };

        // Response handler
        const handleNewResponse = ({ activityId, responseId, answer, participant, createdAt }) => {
            const currentActivity = activityRef.current;
            if (currentActivity && Number(activityId) === Number(currentActivity.id)) {
                console.log('[JoinPage] New response received:', { participant, activityId });
                setResponses(prev => {
                    // Match by responseId, not participant name — a re-submission from the
                    // same session reuses the same row id (the backend upserts on session,
                    // see responseRepository.saveResponse), so this still correctly merges an
                    // update in place. Matching by name instead would incorrectly merge two
                    // DIFFERENT Anonymous participants' responses into one row here, even
                    // though the database stores them as separate rows (see participantSession.js
                    // — identity is per-browser session, not per display name).
                    const existingIndex = responseId != null
                        ? prev.findIndex(r => r.id != null && Number(r.id) === Number(responseId))
                        : -1;

                    const newRow = { id: responseId, answer, participant, created_at: createdAt, reactions: {} };
                    if (existingIndex > -1) {
                        const updated = [...prev];
                        updated[existingIndex] = { ...updated[existingIndex], ...newRow };
                        return updated;
                    }
                    return [...prev, newRow];
                });
            }
        };

        // Handle socket reconnection
        const handleConnect = () => {
            console.log('[JoinPage] Socket connected, re-joining event:', event.id);
            setIsSocketConnected(true);
            socket.emit('participant:join', { eventId: event.id });

            // A disconnect can land at the exact moment the admin switches polls — Socket.IO
            // never replays room broadcasts to a client that was offline when they were sent,
            // so activity:launched/activity:stopped can be missed for good. Re-fetch the
            // event's current active activity on every (re)connect so the participant can't
            // get stuck on a stale poll until they manually reload.
            request(`${API}/join/${event.code}`)
                .then(r => r.json())
                .then(data => {
                    if (data.activeActivity) {
                        const activeActivity = normalizeActivity(data.activeActivity);
                        setActivity(activeActivity);
                        const savedAnswer = sessionStorage.getItem(`lp_answer_${activeActivity.id}`);
                        if (savedAnswer) {
                            try {
                                setUserAnswer(JSON.parse(savedAnswer));
                            } catch {
                                setUserAnswer(null);
                            }
                        }
                        setStep('activity');
                    } else {
                        setActivity(null);
                        setStep('waiting');
                    }
                })
                .catch(() => {});
        };

        const handleDisconnect = () => {
            console.log('[JoinPage] Socket disconnected');
            setIsSocketConnected(false);
        };

        // Attach all listeners
        socket.on('connect', handleConnect);
        socket.on('disconnect', handleDisconnect);
        socket.on('room:update', handleRoomUpdate);
        socket.on('activity:launched', handleActivityLaunched);
        socket.on('activity:stopped', handleActivityStopped);
        socket.on('activity:deleted', handleActivityDeleted);
        socket.on('activity:updated', handleActivityUpdated);
        socket.on('results:cleared', handleResultsCleared);
        socket.on('response:deleted', handleResponseDeleted);
        socket.on('response:reaction', handleResponseReaction);
        socket.on('response:updated', handleResponseUpdated);
        socket.on('response:new', handleNewResponse);

        // Cleanup function
        return () => {
            console.log('[JoinPage] Cleaning up socket listeners');
            socket.off('connect', handleConnect);
            socket.off('disconnect', handleDisconnect);
            socket.off('room:update', handleRoomUpdate);
            socket.off('activity:launched', handleActivityLaunched);
            socket.off('activity:stopped', handleActivityStopped);
            socket.off('activity:deleted', handleActivityDeleted);
            socket.off('activity:updated', handleActivityUpdated);
            socket.off('results:cleared', handleResultsCleared);
            socket.off('response:deleted', handleResponseDeleted);
            socket.off('response:reaction', handleResponseReaction);
            socket.off('response:updated', handleResponseUpdated);
            socket.off('response:new', handleNewResponse);
        };
    }, [socket, event]);

    // Refresh logic: if we have an event in session, re-verify status and restore participant's answer
    useEffect(() => {
        const hasStoredEvent = sessionStorage.getItem(STORAGE_KEYS.event);
        const storedStep = sessionStorage.getItem(STORAGE_KEYS.step);

        if (hasStoredEvent && storedStep && storedStep !== 'code') {
            const eventData = JSON.parse(hasStoredEvent);
            request(`${API}/join/${eventData.code}`)
                .then(r => r.json())
                .then(data => {
                    if (data.activeActivity) {
                        const activeActivity = normalizeActivity(data.activeActivity);
                        setActivity(activeActivity);
                        const savedAnswer = sessionStorage.getItem(`lp_answer_${activeActivity.id}`);
                        if (savedAnswer) {
                            try {
                                setUserAnswer(JSON.parse(savedAnswer));
                            } catch {
                                setUserAnswer(null);
                            }
                        }
                        setStep('activity');
                    } else {
                        setStep('waiting');
                    }
                })
                .catch(() => {});
        }
    }, []);

    // When activity changes, restore participant's saved answer for this activity (so refresh / new activity shows correct state)
    useEffect(() => {
        if (!activity?.id) {
            setUserAnswer(null);
            return;
        }
        const saved = sessionStorage.getItem(`lp_answer_${activity.id}`);
        if (saved) {
            try {
                setUserAnswer(JSON.parse(saved));
            } catch {
                setUserAnswer(null);
            }
        } else {
            setUserAnswer(null);
        }
        setStep('activity');
    }, [activity?.id]);

    // Persist step and voteAsAnonymous
    useEffect(() => {
        sessionStorage.setItem(STORAGE_KEYS.step, step);
    }, [step]);
    useEffect(() => {
        sessionStorage.setItem(STORAGE_KEYS.voteAnonymous, voteAsAnonymous ? 'true' : 'false');
    }, [voteAsAnonymous]);

    // Open profile modal with current values
    const openProfileModal = () => {
        setProfileName(name);
        setProfileAnonymous(voteAsAnonymous);
        setFieldErrors(prev => ({ ...prev, name: '' }));
        setShowProfileModal(true);
    };

    const saveProfile = () => {
        const trimmed = profileName.trim();
        const nameErr = validateName(trimmed, profileAnonymous);
        if (nameErr) {
            setFieldErrors(prev => ({ ...prev, name: nameErr }));
            return;
        }
        setName(trimmed);
        sessionStorage.setItem(STORAGE_KEYS.name, trimmed);
        setVoteAsAnonymous(profileAnonymous);
        sessionStorage.setItem(STORAGE_KEYS.voteAnonymous, profileAnonymous ? 'true' : 'false');
        setShowProfileModal(false);
        setFieldErrors(prev => ({ ...prev, name: '' }));
    };

    const handleJoin = async (e) => {
        e?.preventDefault();
        
        // Validate: name required only when not voting anonymously
        const nameErr = validateName(name, voteAsAnonymous);
        const codeErr = validateCode(code);
        
        if (nameErr || codeErr) {
            setFieldErrors({ name: nameErr, code: codeErr });
            setTouched({ name: true, code: true });
            setError('Please fix the errors above');
            return;
        }

        setError('');
        setLoading(true);
        try {
            const res = await request(`${API}/join/${code.trim().toUpperCase()}`);
            const data = await res.json();
            if (!res.ok) return setError(data.error || 'Event not found');
            
            setEvent(data.event);
            sessionStorage.setItem(STORAGE_KEYS.event, JSON.stringify(data.event));
            sessionStorage.setItem(STORAGE_KEYS.name, name.trim());
            sessionStorage.setItem(STORAGE_KEYS.code, code);
            addToEventHistory(data.event);

            if (data.activeActivity) {
                setActivity(normalizeActivity(data.activeActivity));
                setStep('activity');
            } else {
                setStep('waiting');
            }
        } catch {
            setError('Could not connect to server');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (answer) => {
        if (!activity) return;
        const participantName = voteAsAnonymous ? 'Anonymous' : name.trim();
        try {
            const res = await request(`${API}/join/${event.code}/respond`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    activityId: activity.id,
                    participant: participantName,
                    sessionId: getParticipantSessionId(),
                    answer
                }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || 'Failed to submit answer');
                return;
            }
            // Scratch Card, Slot Machine & Spinner Wheel's outcome is drawn server-side (so it
            // can't be gamed from the client) — read it back from the response. Every other
            // type keeps trusting its own client-computed answer, unchanged from before.
            let resolvedAnswer = answer;
            if (activity.type === 'scratch-card' || activity.type === 'slot-machine' || activity.type === 'spinner-wheel') {
                const data = await res.json().catch(() => null);
                resolvedAnswer = data?.answer ?? answer;
            }
            setUserAnswer(resolvedAnswer);
            setStep('activity');
            sessionStorage.setItem(`lp_answer_${activity.id}`, JSON.stringify(resolvedAnswer));
        } catch {
            setError('Failed to submit answer');
        }
    };

    const handleEditResponse = () => {
        setUserAnswer(null);
        setStep('activity');
    };

    const handleReply = async (responseId, text, parentIndex = null) => {
        if (!activity || !event?.code) return;
        const participantName = voteAsAnonymous ? 'Anonymous' : name.trim();
        try {
            const body = {
                activityId: activity.id,
                responseId,
                text: text.trim(),
                participant: participantName,
                sessionId: getParticipantSessionId(),
            };
            if (parentIndex != null) body.parentIndex = parentIndex;
            const res = await request(`${API}/join/${event.code}/reply`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || 'Failed to send reply');
            }
        } catch {
            setError('Failed to send reply');
        }
    };

    const handleReplyReact = async (responseId, replyIndex, emoji) => {
        if (!activity || !event?.code) return;
        try {
            const res = await request(`${API}/join/${event.code}/reply-react`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    activityId: activity.id,
                    responseId,
                    replyIndex,
                    emoji,
                    sessionId: getParticipantSessionId(),
                }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || 'Failed to update reaction');
            }
        } catch {
            setError('Failed to update reaction');
        }
    };

    // ─── Switch Event handlers ──────────────────────────────────────────────
    const addToEventHistory = (evt) => {
        const newEvent = {
            id: evt.id,
            name: evt.name,
            code: evt.code,
            joinedAt: new Date().toISOString()
        };
        const updated = [newEvent, ...previousEvents].slice(0, 5);
        setPreviousEvents(updated);
        sessionStorage.setItem(STORAGE_KEYS.previousEvents, JSON.stringify(updated));
    };

    const handleSwitchEvent = async (e) => {
        e?.preventDefault();
        if (!switchCode.trim()) {
            setSwitchError('Enter event code');
            return;
        }
        setSwitchError('');
        setSwitchLoading(true);
        try {
            const res = await request(`${API}/join/${switchCode.trim().toUpperCase()}`);
            const data = await res.json();
            if (!res.ok) {
                setSwitchError(data.error || 'Event not found');
                return;
            }
            setEvent(data.event);
            sessionStorage.setItem(STORAGE_KEYS.event, JSON.stringify(data.event));
            sessionStorage.setItem(STORAGE_KEYS.code, switchCode.trim().toUpperCase());
            addToEventHistory(data.event);

            if (data.activeActivity) {
                setActivity(data.activeActivity);
                setStep('activity');
            } else {
                setStep('waiting');
            }
            setShowSwitchModal(false);
            setSwitchCode('');
        } catch {
            setSwitchError('Could not connect to server');
        } finally {
            setSwitchLoading(false);
        }
    };

    const handleQuickSwitchEvent = async (prevEvent) => {
        setSwitchLoading(true);
        try {
            const res = await request(`${API}/join/${prevEvent.code}`);
            const data = await res.json();
            if (!res.ok) {
                setSwitchError(data.error || 'Event not found');
                return;
            }
            setEvent(data.event);
            sessionStorage.setItem(STORAGE_KEYS.event, JSON.stringify(data.event));
            sessionStorage.setItem(STORAGE_KEYS.code, prevEvent.code);
            addToEventHistory(data.event);

            if (data.activeActivity) {
                setActivity(normalizeActivity(data.activeActivity));
                setStep('activity');
            } else {
                setStep('waiting');
            }
            setShowSwitchModal(false);
        } catch {
            setSwitchError('Could not connect to server');
        } finally {
            setSwitchLoading(false);
        }
    };


    return (
        <div className={`${styles.joinRoot} ${showDesktopLayout ? styles.joinRootDesktop : ''}`}>
            {showDesktopLayout && (
                <div className={styles.desktopLeftPanel}>
                    <img src={livePulseLogo} alt="LivePulse" className={styles.desktopBrand} />
                    {event ? (
                        <>
                            <h2 className={styles.desktopEventName}>{event.name}</h2>
                            <p className={styles.desktopEventCode}>Event code: <strong>{event.code}</strong></p>
                            {joinUrl && (
                                <div className={styles.desktopQrCard}>
                                    <img
                                        src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=8&color=0a0a1a&bgcolor=ffffff&data=${encodeURIComponent(joinUrl)}`}
                                        alt={`QR code to join ${event.name} on a phone`}
                                        className={styles.desktopQrImage}
                                        width={160}
                                        height={160}
                                    />
                                    <p className={styles.desktopQrHint}>
                                        <QrCode size={14} aria-hidden /> Prefer your phone? Scan to join there instead.
                                    </p>
                                </div>
                            )}
                            {step !== 'code' && (
                                <div className={styles.desktopLiveCounter}>
                                    <span className={styles.liveDot} data-disconnected={!isSocketConnected} aria-hidden />
                                    {participantsCount} connected
                                </div>
                            )}
                        </>
                    ) : (
                        <p className={styles.desktopTagline}>Real-time audience engagement — join an event to get started.</p>
                    )}
                </div>
            )}
            <div className={`${styles.phoneFrame} ${showDesktopLayout ? styles.phoneFrameDesktop : ''}`}>
                <div className={styles.page}>
            {/* Brand bar */}
            <div className={styles.topBar}>
                <Link to="/" className={styles.brandContainer}>
                    <img src={livePulseLogo} alt="LivePulse" className={styles.brand} />
                </Link>
                <div className={styles.topBarRight}>
                    {event && step !== 'code' && (
                        <div className={styles.liveCounter}>
                            <span
                                className={styles.liveDot}
                                data-disconnected={!isSocketConnected}
                                aria-hidden
                            />
                            {participantsCount} connected
                        </div>
                    )}
                    {event && step !== 'code' && (
                        <Tooltip title="Switch to another event" placement="bottom">
                            <button
                                className={styles.switchBtn}
                                onClick={() => setShowSwitchModal(true)}
                            >
                                <ArrowLeftRight size={12} aria-hidden />
                                Switch Event
                            </button>
                        </Tooltip>
                    )}
                    {!isMobileViewport && (
                        <Tooltip title={showDesktopLayout ? 'Switch to phone view' : 'Switch to desktop view'} placement="bottom">
                            <button
                                className={styles.switchBtn}
                                onClick={toggleDesktopMode}
                                aria-pressed={showDesktopLayout}
                            >
                                {showDesktopLayout ? <Smartphone size={12} aria-hidden /> : <Monitor size={12} aria-hidden />}
                                {showDesktopLayout ? 'Phone View' : 'Desktop Mode'}
                            </button>
                        </Tooltip>
                    )}
                    <div className={styles.themeBtn}>
                        <ThemeToggle />
                    </div>
                </div>
            </div>

            {/* Participant identity bar: "You joined as [Name]" + Edit + Anonymous badge; show when joined (with name or as anonymous) */}
            {event && step !== 'code' && (name.trim() || voteAsAnonymous) && (
                <div className={styles.identityBar}>
                    <div className={styles.identityContent}>
                        <User size={16} className={styles.identityIcon} aria-hidden />
                        <span className={styles.identityLabel}>Joined as</span>
                        {name.trim() ? (
                            <strong className={styles.identityName}>{name.trim()}</strong>
                        ) : null}
                        {voteAsAnonymous && (
                            <span className={styles.anonymousBadge} title="Your votes will appear as Anonymous">
                                Anonymous
                            </span>
                        )}
                    </div>
                    <Tooltip title="Change name or anonymous setting" placement="bottom">
                        <button
                            type="button"
                            className={styles.identityEditBtn}
                            onClick={openProfileModal}
                            aria-label="Edit your profile"
                        >
                            <Pencil size={14} />
                            Edit
                        </button>
                    </Tooltip>
                </div>
            )}

            {/* Profile modal: change name + Vote as Anonymous */}
            {showProfileModal && (
                <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-label="Your profile">
                    <div className={styles.modal}>
                        <div className={styles.modalHeader}>
                            <h2 className={styles.modalTitle}>Your profile</h2>
                            <button
                                type="button"
                                className={styles.modalClose}
                                onClick={() => setShowProfileModal(false)}
                                aria-label="Close"
                            >
                                <X size={18} aria-hidden />
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <div className={styles.modalSection}>
                                <label className={styles.sectionTitle} htmlFor="profile-name">Display name</label>
                                <input
                                    id="profile-name"
                                    type="text"
                                    className={styles.switchInput}
                                    value={profileName}
                                    onChange={(e) => setProfileName(e.target.value)}
                                    placeholder="Your name"
                                    maxLength={40}
                                    autoFocus
                                />
                                {fieldErrors.name && (
                                    <span className={styles.errorText} role="alert">{fieldErrors.name}</span>
                                )}
                            </div>
                            <div className={styles.modalSection}>
                                <label className={styles.profileCheckLabel}>
                                    <input
                                        type="checkbox"
                                        checked={profileAnonymous}
                                        onChange={(e) => setProfileAnonymous(e.target.checked)}
                                        className={styles.profileCheckbox}
                                    />
                                    <span>Vote as Anonymous</span>
                                </label>
                                <p className={styles.profileCheckHint}>
                                    Your responses will show as &quot;Anonymous&quot; to the host and in results.
                                </p>
                            </div>
                            <div className={styles.modalFooter}>
                                <button
                                    type="button"
                                    className={styles.profileCancelBtn}
                                    onClick={() => setShowProfileModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className={styles.profileSaveBtn}
                                    onClick={saveProfile}
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Switch Event Modal */}
            {showSwitchModal && (
                <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-label="Switch event">
                    <div className={styles.modal}>
                        <div className={styles.modalHeader}>
                            <h2 className={styles.modalTitle}>Switch Event</h2>
                            <button
                                className={styles.modalClose}
                                onClick={() => setShowSwitchModal(false)}
                                aria-label="Close"
                            >
                                <X size={18} aria-hidden />
                            </button>
                        </div>

                        <div className={styles.modalBody}>
                            {/* Current Event Card */}
                            {event && (
                                <div className={styles.modalSection}>
                                    <div className={styles.sectionTitle}>Currently on</div>
                                    <div className={styles.currentEventCard}>
                                        <div className={styles.eventCardTitle}>{event.name}</div>
                                        <div className={styles.eventCardCode}>#{event.code}</div>
                                    </div>
                                </div>
                            )}

                            {/* Code Input Section */}
                            <div className={styles.modalSection}>
                                <div className={styles.sectionTitle}>Enter new event code</div>
                                <form onSubmit={handleSwitchEvent} className={styles.switchForm}>
                                    <input
                                        type="text"
                                        className={styles.switchInput}
                                        placeholder="Event code..."
                                        value={switchCode}
                                        onChange={(e) => setSwitchCode(e.target.value.toUpperCase())}
                                        maxLength={6}
                                        disabled={switchLoading}
                                    />
                                    <button
                                        type="submit"
                                        className={styles.switchSubmitBtn}
                                        disabled={switchLoading || !switchCode.trim()}
                                        aria-label="Submit event code"
                                    >
                                        {switchLoading ? <Loader2 size={14} className={styles.spinner} /> : <ArrowRight size={16} aria-hidden />}
                                    </button>
                                </form>
                                {switchError && <div className={styles.switchError}>{switchError}</div>}
                            </div>

                            {/* Previous Events */}
                            {previousEvents.length > 0 && (
                                <div className={styles.modalSection}>
                                    <div className={styles.sectionTitle}>Recent events</div>
                                    <div className={styles.eventsList}>
                                        {previousEvents.map((evt) => (
                                            <button
                                                key={evt.code}
                                                className={styles.eventItem}
                                                onClick={() => handleQuickSwitchEvent(evt)}
                                                disabled={switchLoading || evt.code === event?.code}
                                            >
                                                <div className={styles.eventItemName}>{evt.name}</div>
                                                <div className={styles.eventItemCode}>#{evt.code}</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className={styles.pageBody}>
            <div className={styles.card} style={step !== 'code' ? { display: 'none' } : {}}>
                <form onSubmit={handleJoin} className={styles.form}>
                    <div className={styles.emoji}><Target size={32} aria-hidden /></div>
                    <h1 className={styles.title}>Join an Event</h1>
                    <p className={styles.subtitle}>Enter your name and the event code to participate</p>

                    {/* Name Input */}
                    <div className={`${styles.inputGroup} ${fieldErrors.name && touched.name ? styles.inputGroupError : ''}`}>
                        <input
                            ref={codeRef}
                            type="text"
                            className={styles.input}
                            placeholder="Your name"
                            value={name}
                            onChange={handleNameChange}
                            onBlur={() => handleFieldBlur('name')}
                            maxLength={40}
                            aria-invalid={!!(fieldErrors.name && touched.name)}
                        />
                        <p className={styles.fieldHint}>Use the name you want the host and participants to see.</p>
                        {fieldErrors.name && touched.name && (
                            <span className={styles.errorText} role="alert">
                                {fieldErrors.name}
                            </span>
                        )}
                    </div>

                    {/* Code Input */}
                    <div className={`${styles.inputGroup} ${fieldErrors.code && touched.code ? styles.inputGroupError : ''}`}>
                        <input
                            type="text"
                            className={styles.input}
                            placeholder="EVENT CODE"
                            value={code}
                            onChange={handleCodeChange}
                            onBlur={() => handleFieldBlur('code')}
                            maxLength={6}
                            inputMode="text"
                            autoCapitalize="characters"
                            aria-invalid={!!(fieldErrors.code && touched.code)}
                        />
                        <p className={styles.fieldHint}>Event codes are 6 characters. Example: `A1B2C3`.</p>
                        {fieldErrors.code && touched.code && (
                            <span className={styles.errorText} role="alert">
                                {fieldErrors.code}
                            </span>
                        )}
                    </div>

                    <div className={styles.joinOptionRow}>
                        <label className={styles.joinCheckLabel}>
                            <input
                                type="checkbox"
                                checked={voteAsAnonymous}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setVoteAsAnonymous(checked);
                                    if (checked) setFieldErrors(prev => ({ ...prev, name: '' }));
                                }}
                                className={styles.joinCheckbox}
                            />
                            <span>Vote as Anonymous</span>
                        </label>
                        <p className={styles.joinCheckHint}>
                            Your responses will appear as &quot;Anonymous&quot; to the host.
                        </p>
                    </div>

                    {error && <div className={styles.error} role="alert">{error}</div>}

                    <button className={styles.joinBtn} disabled={loading} type="submit">
                        {loading ? (
                            <>
                                <Loader2 size={16} className={styles.spinner} /> Joining…
                            </>
                        ) : (
                            <>
                                Join <ArrowRight size={18} aria-hidden />
                            </>
                        )}
                    </button>
                </form>
            </div>

            {step !== 'code' && (
                <ParticipantExperience
                    step={step}
                    name={name}
                    event={event}
                    activity={activity}
                    responses={responses}
                    userAnswer={userAnswer}
                    onSubmit={handleSubmit}
                    onEditResponse={handleEditResponse}
                    onReact={handleReact}
                    onReply={handleReply}
                    onReplyReact={handleReplyReact}
                />
            )}
            </div>
                </div>
            </div>
        </div>
    );
};

export default JoinPage;
